import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  ExplanationData,
  saveCalculationWithNotes,
  getMotorcycleCommercialCalculation,
  MotorcycleCommercialCalculationResponse,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import ActualCostCalculator, {
  ActualCostData,
} from "../../shared/ActualCostCalculator";

// ============================================================
// Interfaces
// ============================================================

interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
  engine_capacity: string;
}

interface SafetyFeatures {
  abs: boolean;
  stability_control: boolean;
  integrated_braking: boolean;
  steering_damper: boolean;
  traction_control: boolean;
  three_wheeled: boolean;
}

interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}

// ============================================================
// Constants
// ============================================================

// מפתחות השדות לטולטיפס הסבר
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
  engine_capacity: "engine_capacity",
} as const;

// אופציות נפח מנוע
const ENGINE_OPTIONS = [
  { value: "", label: "בחר סוג מנוע", disabled: true },
  { value: "25", label: 'אינו עולה על 50 סמ"ק' },
  { value: "125", label: 'אינו עולה על 125 סמ"ק' },
  { value: "250", label: 'עולה על 125 סמ"ק אך אינו עולה על 250 סמ"ק' },
  { value: "500", label: 'עולה על 250 סמ"ק אך אינו עולה על 500 סמ"ק' },
  { value: "800", label: 'עולה על 500 סמ"ק אך אינו עולה על 800 סמ"ק' },
  { value: "1000", label: 'עולה על 800 סמ"ק' },
  { value: "hybrid", label: "היברידי" },
  { value: "0", label: "מנוע חשמלי" },
];

// ============================================================
// Component
// ============================================================

const MotorcycleCommercialCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  // ============================================================
  // State Management
  // ============================================================

  const [currentStep, setCurrentStep] = useState(1);

  // נתוני טופס
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
    engine_capacity: "",
  });

  // אבזור בטיחותי - חלק מהשלב הראשון!
  const [safetyFeatures, setSafetyFeatures] = useState<SafetyFeatures>({
    abs: false,
    stability_control: false,
    integrated_braking: false,
    steering_damper: false,
    traction_control: false,
    three_wheeled: false,
  });

  // נתוני עזר
  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});

  // מצב חישוב
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [calculationResult, setCalculationResult] =
    useState<MotorcycleCommercialCalculationResponse | null>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // שמירה
  const [userNotes, setUserNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [commercialActualCostData, setCommercialActualCostData] =
    useState<ActualCostData | null>(null);
  // ============================================================
  // Effects
  // ============================================================

  // טעינת נתוני מחשבון בטעינה ראשונית
  useEffect(() => {
    loadCalculatorData();
  }, []);
  const loadCalculatorData = async () => {
    try {
      setIsLoading(true);
      const response = await calculatorService.getCalculatorData();

      if (response.res === "OK" && response.data) {
        setCurrencies(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
      } else {
        setError("שגיאה בטעינת נתוני המחשבון");
      }
    } catch (error) {
      setError("שגיאה בחיבור לשרת");
      console.error("Error loading calculator data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // Input Handlers
  // ============================================================

  // טיפול בשינוי שדות
  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");

    // עדכון אוטומטי של שער חליפין
    if (field === "currency_type" && typeof value === "string") {
      const selectedCurrency = currencies.find(
        (curr) => curr.currency_code === value,
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          exchange_rate: Number(selectedCurrency.customs_rate).toFixed(4),
          insurance_currency_type: value,
          insurance_exchange_rate: Number(
            selectedCurrency.customs_rate,
          ).toFixed(4),
        }));
      }
    }
  };

  // טיפול בשינוי מספרים (למניעת אפסים מוזרים)
  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0)) {
      handleInputChange(field, value);
    }
  };

  // בדיקה אם המנוע חשמלי
  const isElectricEngine = () => {
    return formData.engine_capacity === "0";
  };

  // ============================================================
  // Calculation Handler - הקריטי!
  // ============================================================

  const handleCalculate = async () => {
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }
    try {
      setIsLoading(true);
      setError("");

      // בדיקת שדות חובה
      // בדיקת שדות חובה
      if (!formData.manufacturing_date) {
        setError("נא להזין תאריך ייצור");
        return;
      }

      if (!formData.engine_capacity) {
        setError("נא לבחור סוג מנוע");
        return;
      }

      if (!formData.vehicle_price_foreign) {
        setError("נא להזין ערך האופנוע");
        return;
      }

      if (!formData.currency_type) {
        setError("נא לבחור סוג מטבע");
        return;
      }

      if (!formData.exchange_rate) {
        setError("נא להזין שער חליפין");
        return;
      }

      // עלות הובלה - חובה (גם 0 מתקבל)
      if (formData.shipping_cost_foreign === "") {
        setError("נא להזין עלות הובלה (ניתן להזין 0)");
        return;
      }

      // הוצאות מקומיות - חובה (גם 0 מתקבל)
      if (formData.local_expenses_ils === "") {
        setError("נא להזין הוצאות מקומיות (ניתן להזין 0)");
        return;
      }

      // בניית בקשה - למנוע חשמלי נשלח "electric"
      const engineCapacity = isElectricEngine()
        ? "electric"
        : formData.engine_capacity === "hybrid"
          ? "hybrid"
          : parseFloat(formData.engine_capacity);

      // 🔴 ההבדל המרכזי - שליחת נתוני אבזור בטיחותי!
      const calculationRequest: any = {
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) ||
          parseFloat(formData.exchange_rate),
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        has_origin_declaration: formData.has_origin_declaration,
        engine_capacity: engineCapacity,
        safety_features: safetyFeatures, // 🔴 שליחת האבזור!
      };

      console.log("🏢 שליחת בקשה לחישוב מסחרי עם אבזור:", calculationRequest);

      // 🔴 שימוש בפונקציה החדשה למסחרי!
      const response =
        await getMotorcycleCommercialCalculation(calculationRequest);

      if (response.res === "OK" && response.data) {
        console.log("✅ תגובה מוצלחת:", response.data);
        setCalculationResult(response.data);
        setCurrentStep(2); // מעבר לתוצאות
      } else {
        setError(response.msg || "שגיאה בחישוב האופנוע");
      }
    } catch (error) {
      console.error("❌ Calculation error:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setIsLoading(false);
    }
  };
  const getTaxRateByEngineCapacity = (): number => {
    const capacity = formData.engine_capacity;
    if (capacity === "25" || capacity === "125") return 25;
    if (capacity === "250") return 40;
    if (capacity === "500") return 50;
    if (capacity === "800") return 60;
    if (capacity === "1000") return 70;
    return 0; // מנוע חשמלי או לא נבחר
  };

  // פונקציה לחישוב הנחה אפשרית (בשלב 1 - לפני החישוב)
  const calculateEstimatedDiscount = (): number => {
    const taxRate = getTaxRateByEngineCapacity();
    if (taxRate === 0) return 0; // מנוע חשמלי או לא נבחר

    let discount = 0;

    // ABS - רק למנועים 25%
    if (taxRate === 25 && safetyFeatures.abs) {
      discount += 500;
    }

    // Stability Control
    if (safetyFeatures.stability_control) {
      if (taxRate === 25) discount += 220;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }

    // Integrated Braking
    if (safetyFeatures.integrated_braking) {
      if (taxRate === 25) discount += 0;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }

    // Steering Damper
    if (safetyFeatures.steering_damper) {
      if (taxRate === 25) discount += 220;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }

    // Traction Control
    if (safetyFeatures.traction_control) {
      if (taxRate === 25) discount += 500;
      else if (taxRate === 40) discount += 650;
      else if (taxRate === 50) discount += 800;
      else if (taxRate === 60) discount += 970;
      else if (taxRate === 70) discount += 1130;
    }

    // Three Wheeled
    if (safetyFeatures.three_wheeled) {
      if (taxRate === 25) discount += 1300;
      else if (taxRate === 40) discount += 2000;
      else if (taxRate === 50) discount += 2300;
      else if (taxRate === 60) discount += 3000;
      else if (taxRate === 70) discount += 3000;
    }

    return discount;
  };
  // ============================================================
  // Save Handler
  // ============================================================

  const handleSaveCalculation = async () => {
    if (!calculationResult) return;

    setIsSaving(true);
    try {
      // 🔴 שמירה עם כל הנתונים מה-API (כולל חישוב אבזור!)
      const saveRequest: SaveCalculationRequest = {
        calculation_type: "motorcycle_commercial",
        input_data: calculationResult.input_data,
        result_data: {
          ...calculationResult,
          // כל הנתונים כבר מגיעים מחושבים מה-API!
          safety_equipment: calculationResult.safety_equipment,
          final_results: calculationResult.final_results,
          commercial_actual_cost_data: commercialActualCostData,
        },
        notes: userNotes.trim(),
      };

      console.log("💾 שמירת חישוב מסחרי:", saveRequest);

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        console.log("✅ החישוב נשמר בהצלחה");
        setTimeout(() => navigate("/enduser/dashboard"), 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("❌ Save error:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================
  // Utility Functions
  // ============================================================

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון אופנועים - ייבוא מסחרי",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  // פונקציית וולידציית תאריך (מקסימום 2 שנים אחורה)
  const validateManufacturingDate = (date: string): boolean => {
    if (!date) return false;
    const manufacturingDate = new Date(date);
    const currentDate = new Date();
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(currentDate.getFullYear() - 2);
    return manufacturingDate >= twoYearsAgo && manufacturingDate <= currentDate;
  };

  // פונקציה לקבלת שם קבוצת המנוע
  const getSelectedEngineGroupName = (): string => {
    const selectedOption = ENGINE_OPTIONS.find(
      (option) => option.value === formData.engine_capacity,
    );
    return selectedOption ? selectedOption.label : "";
  };

  // ============================================================
  // Render Conditions
  // ============================================================

  // מסך טעינה
  if (isLoading && currentStep === 1) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען נתוני מחשבון...</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 text-6xl"></div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון אופנועים - ייבוא מסחרי
          </h1>
          <p className="text-gray-600">
            חישוב מיסים עבור אופנועים ייבוא מסחרי (כולל חישוב אבזור בטיחותי)
          </p>

          {/* Progress bar */}
          <div className="mt-6 mb-8">
            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                1
              </div>
              <div
                className={`h-1 w-16 ${
                  currentStep >= 2 ? "bg-blue-600" : "bg-gray-200"
                }`}
              ></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 2
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                2
              </div>
            </div>
            <div className="flex justify-center mt-2 space-x-8 rtl:space-x-reverse">
              <span
                className={`text-sm ${
                  currentStep >= 1
                    ? "text-blue-600 font-medium"
                    : "text-gray-500"
                }`}
              >
                נתוני קלט + אבזור
              </span>
              <span
                className={`text-sm ${
                  currentStep >= 2
                    ? "text-blue-600 font-medium"
                    : "text-gray-500"
                }`}
              >
                תוצאות
              </span>
            </div>
          </div>
        </div>

        {/* הודעת שגיאה */}
        {/* {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-center">{error}</p>
          </div>
        )} */}

        {/* ============================================================ */}
        {/* שלב 1: טופס הקלט + אבזור בטיחותי */}
        {/* ============================================================ */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms-calculator"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setError("");
                  }}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="terms-calculator"
                  className="text-sm text-gray-700 flex-1"
                >
                  קראתי ואני מאשר/ת את{" "}
                  <Link
                    to="/terms-and-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    תנאי השימוש ומדיניות הפרטיות
                  </Link>{" "}
                  *
                </label>
              </div>
            </div>
            {/* טופס נתוני האופנוע */}
            <MotionCard className="p-8">
              <h2 className="text-xl font-bold mb-6 text-center">
                שלב 1א: פרטי האופנוע
              </h2>

              <div className="space-y-6">
                {/* תאריך ייצור */}
                <div>
                  <label
                    htmlFor="manufacturing_date"
                    className="flex items-center mb-2 font-medium"
                  >
                    תאריך ייצור האופנוע{" "}
                    <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.manufacturing_date}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="manufacturing_date"
                    type="date"
                    value={formData.manufacturing_date}
                    onChange={(e) =>
                      handleInputChange("manufacturing_date", e.target.value)
                    }
                    aria-required="true"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    max={new Date().toISOString().split("T")[0]}
                    min={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 2),
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                  />
                  {formData.manufacturing_date &&
                    !validateManufacturingDate(formData.manufacturing_date) && (
                      <p className="text-red-600 text-sm mt-1">
                        מחשבון זה מיועד לאופנועים עד שנתיים
                      </p>
                    )}
                </div>

                {/* סוג מנוע */}
                <div>
                  <label
                    htmlFor="engine_capacity"
                    className="flex items-center mb-2 font-medium"
                  >
                    סוג מנוע <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.engine_capacity}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="engine_capacity"
                    value={formData.engine_capacity}
                    onChange={(e) =>
                      handleInputChange("engine_capacity", e.target.value)
                    }
                    aria-required="true"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {ENGINE_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formData.engine_capacity && (
                    <p className="text-sm text-gray-600 mt-1">
                      {isElectricEngine()
                        ? "⚡ מנוע חשמלי - ללא פליטות"
                        : `נבחר: ${getSelectedEngineGroupName()}`}
                    </p>
                  )}
                </div>

                {/* ערך האופנוע */}
                <div>
                  <label
                    htmlFor="vehicle_price_foreign"
                    className="flex items-center mb-2 font-medium"
                  >
                    ערך האופנוע במטבע זר{" "}
                    <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.vehicle_price_foreign}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="vehicle_price_foreign"
                    type="number"
                    value={formData.vehicle_price_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "vehicle_price_foreign",
                        e.target.value,
                      )
                    }
                    aria-required="true"
                    placeholder="למשל: 15000"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>

                {/* סוג מטבע */}
                <div>
                  <label
                    htmlFor="currency_type"
                    className="flex items-center mb-2 font-medium"
                  >
                    סוג מטבע <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.currency_type}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="currency_type"
                    value={formData.currency_type}
                    onChange={(e) =>
                      handleInputChange("currency_type", e.target.value)
                    }
                    aria-required="true"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">בחר מטבע</option>
                    {currencies.map((currency) => (
                      <option
                        key={currency.currency_code}
                        value={currency.currency_code}
                      >
                        {currency.currency_name_hebrew} (
                        {currency.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער חליפין */}
                <div>
                  <label
                    htmlFor="exchange_rate"
                    className="flex items-center mb-2 font-medium"
                  >
                    שער חליפין <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.exchange_rate}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="exchange_rate"
                    type="number"
                    step="0.0001"
                    value={formData.exchange_rate}
                    onChange={(e) =>
                      handleNumberInputChange("exchange_rate", e.target.value)
                    }
                    aria-required="true"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    placeholder="0.0000"
                  />
                </div>

                {/* עלות הובלה */}
                <div>
                  <label
                    htmlFor="shipping_cost_foreign"
                    className="flex items-center mb-2 font-medium"
                  >
                    עלות הובלה במטבע המקור{" "}
                    <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.shipping_cost_foreign}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="shipping_cost_foreign"
                    type="number"
                    value={formData.shipping_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "shipping_cost_foreign",
                        e.target.value,
                      )
                    }
                    aria-required="true"
                    placeholder="0"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* עלות ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_cost_foreign"
                    className="flex items-center mb-2 font-medium"
                  >
                    עלות ביטוח במטבע המקור
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_cost_foreign}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="insurance_cost_foreign"
                    type="number"
                    value={formData.insurance_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_cost_foreign",
                        e.target.value,
                      )
                    }
                    placeholder="0"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* מטבע ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_currency_type"
                    className="flex items-center mb-2 font-medium"
                  >
                    מטבע ביטוח
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_currency_type}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="insurance_currency_type"
                    value={formData.insurance_currency_type}
                    onChange={(e) =>
                      handleInputChange(
                        "insurance_currency_type",
                        e.target.value,
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">בחר מטבע ביטוח</option>
                    {currencies.map((currency) => (
                      <option
                        key={currency.currency_code}
                        value={currency.currency_code}
                      >
                        {currency.currency_name_hebrew} (
                        {currency.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_exchange_rate"
                    className="flex items-center mb-2 font-medium"
                  >
                    שער חליפין ביטוח
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_exchange_rate}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="insurance_exchange_rate"
                    type="number"
                    step="0.0001"
                    value={formData.insurance_exchange_rate}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_exchange_rate",
                        e.target.value,
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    placeholder="0.0000"
                  />
                </div>

                {/* הוצאות מקומיות */}
                <div>
                  <label
                    htmlFor="local_expenses_ils"
                    className="flex items-center mb-2 font-medium"
                  >
                    הוצאות מקומיות בשקלים{" "}
                    <span className="text-red-500 mr-1">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.local_expenses_ils}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="local_expenses_ils"
                    type="number"
                    value={formData.local_expenses_ils}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "local_expenses_ils",
                        e.target.value,
                      )
                    }
                    aria-required="true"
                    placeholder="0"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* הצהרת מקור */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={formData.has_origin_declaration}
                      onChange={(e) =>
                        handleInputChange(
                          "has_origin_declaration",
                          e.target.checked,
                        )
                      }
                      className="w-5 h-5 text-blue-600"
                    />
                    <span className="font-medium">יש הצהרת/תעודת מקור</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.has_origin_declaration}
                      explanations={explanations}
                    />
                  </label>
                  <p className="text-sm text-gray-600 mt-2 mr-8">
                    {formData.has_origin_declaration
                      ? "✅ פטור ממכס (0%)"
                      : "⚠️ חייב במכס 7%"}
                  </p>
                </div>
              </div>
            </MotionCard>

            {/* 🔥 אבזור בטיחותי - בשלב 1! (ההבדל המרכזי!) */}
            {!isElectricEngine() && (
              <MotionCard className="p-8 border-2 border-orange-200 bg-orange-50">
                <h2 className="text-xl font-bold mb-4 text-orange-800">
                  🛡️ שלב 1ב: אבזור בטיחותי
                </h2>
                <p className="text-sm text-orange-700 mb-4">
                  סמן את האבזור הבטיחותי הקיים באופנוע. ההנחה תחושב ותשוקלל
                  אוטומטית במחיר הסופי.
                </p>

                <div className="space-y-3 bg-white p-4 rounded-lg">
                  {/* ABS - רק למנועים 25% */}
                  {formData.engine_capacity &&
                    (formData.engine_capacity === "25" ||
                      formData.engine_capacity === "125") && (
                      <label className="flex items-center space-x-3 rtl:space-x-reverse">
                        <input
                          type="checkbox"
                          checked={safetyFeatures.abs}
                          onChange={(e) =>
                            setSafetyFeatures((prev) => ({
                              ...prev,
                              abs: e.target.checked,
                            }))
                          }
                          className="w-4 h-4 text-orange-600"
                        />
                        <span className="text-sm font-medium">
                          מערכת ABS (רק עד 125 סמ"ק) - הנחה: 500 ש"ח
                        </span>
                      </label>
                    )}

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.stability_control}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          stability_control: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium">
                      מערכת בקרת יציבות
                    </span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.integrated_braking}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          integrated_braking: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium">
                      מערכת בלימה משולבת
                    </span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.steering_damper}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          steering_damper: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium">משכך היגוי</span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.traction_control}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          traction_control: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium">
                      מערכת בקרת משיכה והחלקה
                    </span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.three_wheeled}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          three_wheeled: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium">
                      אופנוע תלת גלגלי
                    </span>
                  </label>
                </div>
                {calculateEstimatedDiscount() > 0 && (
                  <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg">
                    <p className="font-semibold text-green-800 mb-1">
                      הנחה אפשרית:{" "}
                      {calculateEstimatedDiscount().toLocaleString()} ש"ח
                    </p>
                    <p className="text-xs text-green-700">
                      💡 ההנחה הסופית תהיה עד גובה מס הקניה ותשוקלל אוטומטית
                      במחיר
                    </p>
                  </div>
                )}
                {safetyFeatures.three_wheeled && (
                  <div className="mt-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <h4 className="font-semibold text-yellow-800 mb-2">
                      לעניין זה, "רכב תלת גלגלי" הוא רכב שמתקיימים לגביו כל
                      התנאים האלה:
                    </h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• יש לו שני גלגלים קדמיים ואחד מאחור</li>
                      <li>
                        • המרחק בין מרכז שני הגלגלים הקדמיים שלו לא יפחת מ-35
                        ס"מ ולא יעלה על 70 ס"מ
                      </li>
                      <li>
                        • מערכת המתלים של הגלגלים הקדמיים מאפשרת אי-תלות בזמן
                        תנועה, בין תנועת שני הגלגלים בכל הצירים
                      </li>
                    </ul>
                  </div>
                )}

                <p className="text-xs text-orange-800 mt-4">
                  💡 ההנחה תחושב אוטומטית ותשוקלל במחיר הסופי (עד גובה מס הקניה)
                </p>
              </MotionCard>
            )}
            {/* הודעת שגיאה */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-center">{error}</p>
              </div>
            )}
            {/* כפתורי פעולה */}
            <div className="flex justify-between">
              <MotionButton
                onClick={() =>
                  navigate("/enduser/calculators/commercial-import")
                }
                variant="secondary"
                className="px-6 py-3"
              >
                ← חזור
              </MotionButton>

              <MotionButton
                onClick={handleCalculate}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
              >
                {isLoading ? "מחשב..." : "חשב מיסים →"}
              </MotionButton>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* שלב 2: תוצאות */}
        {/* ============================================================ */}
        {currentStep === 2 && calculationResult && (
          <div className="space-y-6">
            {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי" וכרטיס השמירה) */}
            <div ref={printRef} className="space-y-6">
            <TaxYearNotice />
            {/* נתוני קלט */}
            <MotionCard className="p-6">
              <h3 className="font-bold text-lg mb-4 text-blue-800">
                📋 נתוני הקלט שהוזנו:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-blue-50 p-4 rounded-lg">
                <div>
                  <strong>תאריך ייצור:</strong> {formData.manufacturing_date}
                </div>
                <div>
                  <strong>סוג מנוע:</strong> {getSelectedEngineGroupName()}
                </div>
                <div>
                  <strong>ערך האופנוע:</strong> {formData.vehicle_price_foreign}{" "}
                  {formData.currency_type}
                </div>
                <div>
                  <strong>שער חליפין:</strong> {formData.exchange_rate}
                </div>
                <div>
                  <strong>עלות הובלה:</strong>{" "}
                  {formData.shipping_cost_foreign || "0"}{" "}
                  {formData.currency_type}
                </div>
                <div>
                  <strong>עלות ביטוח:</strong>{" "}
                  {formData.insurance_cost_foreign || "0"}{" "}
                  {formData.insurance_currency_type || formData.currency_type}
                </div>
                <div>
                  <strong>הוצאות מקומיות:</strong>{" "}
                  {formData.local_expenses_ils || "0"} ש"ח
                </div>
                <div>
                  <strong>הצהרת מקור:</strong>{" "}
                  {formData.has_origin_declaration ? "✅ יש" : "❌ אין"}
                </div>
              </div>
            </MotionCard>

            {/* תוצאות החישוב */}
            <MotionCard className="p-6">
              <h3 className="font-bold text-lg mb-6 text-center">
                תוצאות החישוב
              </h3>

              <div className="space-y-4 text-black">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">1. ערך לצרכי מכס:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.total_before_customs.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">2. סך המכס:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.customs_fee.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">3. שיעור המכס:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.customs_rate_percent.toFixed(
                      2,
                    )}
                    %
                  </span>
                </div>

                {/* 🔥 הצגת מס קניה לפני והנחה */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">4. מס קניה לפני הנחה:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.purchase_tax_before_discount.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                {calculationResult.safety_equipment.discount_amount > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">4א. הנחת אבזור בטיחותי:</span>
                    <span className="font-bold text-green-700">
                      -
                      {calculationResult.safety_equipment.discount_amount.toLocaleString()}{" "}
                      ש"ח
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">
                    5. מס קניה סופי (אחרי הנחה):
                  </span>
                  <span className="font-bold text-green-700">
                    {calculationResult.intermediate_calculations.purchase_tax.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">6. שיעור מס הקניה המלא:</span>
                  <span className="font-bold">
                    {
                      calculationResult.intermediate_calculations.engine_info
                        .tax_rate_percent
                    }
                    %
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">7. סך המע"מ:</span>
                  <span className="font-bold">
                    {calculationResult.final_results.vat.toLocaleString()} ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">8. שיעור המע"מ:</span>
                  <span className="font-bold">18%</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">9. סך המיסים:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.total_taxes.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">10. שיעור המיסים:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.tax_percentage.toFixed(
                      1,
                    )}
                    %
                  </span>
                </div>

                {/* 🔥 עלות סופית */}
                <div className="flex justify-between items-center py-3 border-t-2 border-gray-800 bg-gray-50 px-4 rounded-lg">
                  <span className="font-bold text-lg">
                    11. עלות הרכב כולל מיסים:
                  </span>
                  <span className="font-bold text-xl text-green-700">
                    {calculationResult.final_results.final_total.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>
              </div>
            </MotionCard>

            {/* פרטי אבזור בטיחותי */}
            {calculationResult.safety_equipment.is_eligible && (
              <MotionCard className="p-6 bg-orange-50 border-2 border-orange-200">
                <h3 className="font-bold text-lg mb-4 text-orange-800">
                  🛡️ פירוט אבזור בטיחותי
                </h3>

                <div className="space-y-2 text-sm">
                  {calculationResult.safety_equipment.features.abs && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>מערכת ABS</span>
                    </div>
                  )}
                  {calculationResult.safety_equipment.features
                    .stability_control && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>מערכת בקרת יציבות</span>
                    </div>
                  )}
                  {calculationResult.safety_equipment.features
                    .integrated_braking && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>מערכת בלימה משולבת</span>
                    </div>
                  )}
                  {calculationResult.safety_equipment.features
                    .steering_damper && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>משכך היגוי</span>
                    </div>
                  )}
                  {calculationResult.safety_equipment.features
                    .traction_control && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>מערכת בקרת משיכה והחלקה</span>
                    </div>
                  )}
                  {calculationResult.safety_equipment.features
                    .three_wheeled && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-green-600">✅</span>
                      <span>אופנוע תלת גלגלי</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 p-3 bg-orange-100 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>הנחה שהתקבלה:</strong>{" "}
                    {calculationResult.safety_equipment.discount_amount.toLocaleString()}{" "}
                    ש"ח
                    {calculationResult.safety_equipment.discount_amount >=
                      calculationResult.safety_equipment
                        .max_discount_allowed && (
                      <span className="text-xs block mt-1">
                        (הגבלה: עד גובה מס הקניה)
                      </span>
                    )}
                  </p>
                </div>
              </MotionCard>
            )}

            {/* דייק לי את התוצאה */}
            {calculationResult && (
              <ActualCostCalculator
                mode="commercial"
                vehicleCurrency={formData.currency_type}
                vehicleExchangeRate={formData.exchange_rate}
                vehiclePriceForeign={formData.vehicle_price_foreign}
                shippingCostForeign={formData.shipping_cost_foreign}
                insuranceCurrency={formData.insurance_currency_type}
                insuranceExchangeRate={formData.insurance_exchange_rate}
                insuranceCostForeign={formData.insurance_cost_foreign}
                localExpensesIls={formData.local_expenses_ils}
                totalTaxes={
                  (calculationResult?.intermediate_calculations?.customs_fee ??
                    0) +
                  (calculationResult?.intermediate_calculations?.purchase_tax ??
                    0) +
                  (calculationResult?.final_results?.vat ?? 0)
                }
                onDataChange={setCommercialActualCostData}
                explanations={explanations}
                initialData={commercialActualCostData}
              />
            )}
            </div>
            {/* סוף אזור ההדפסה */}

            {/* שמירה והערות */}
            <MotionCard className="p-6 print-exclude">
              <h3 className="font-bold text-lg mb-4 text-gray-800">
                💾 שמירת החישוב
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  הערות נוספות (אופציונלי):
                </label>
                <textarea
                  value={userNotes}
                  onChange={(e) =>
                    e.target.value.length <= 1000 &&
                    setUserNotes(e.target.value)
                  }
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="הוסף הערות על החישוב, פרטים נוספים על האופנוע וכו'..."
                />
                <div className="text-sm text-gray-500 mt-1">
                  {userNotes.length}/1000 תווים
                </div>
              </div>

              <div className="flex justify-between items-center mt-8">
                <MotionButton
                  onClick={() => setCurrentStep(1)}
                  variant="secondary"
                  disabled={isSaving || isSaved}
                  className="px-6 py-3"
                >
                  ← ערוך נתונים
                </MotionButton>

                <MotionButton
                  onClick={handlePrint}
                  variant="secondary"
                  disabled={isSaving || isSaved}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200"
                >
                  🖨️ הדפס
                </MotionButton>

                <MotionButton
                  onClick={handleSaveCalculation}
                  disabled={isSaving || isSaved}
                  className={`${
                    isSaved
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  } text-white px-6 py-3`}
                >
                  {isSaving
                    ? "שומר..."
                    : isSaved
                      ? "נשמר ✓"
                      : "שמור וחזור לדף הבית"}
                </MotionButton>
              </div>
            </MotionCard>
          </div>
        )}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotorcycleCommercialCalculatorPage;
